import React, { useState } from "react";
import * as Mui from "@mui/material";
import { AccountBalanceOutlined, CurrencyRupeeOutlined, FunctionsOutlined, GroupsOutlined, PersonOutlineOutlined } from "@mui/icons-material";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { detailCardSx } from "components/DesignSystem/RequestDetailField";
import { designColors, designType, designWeights } from "services/constants/designTokens";
import { SimpleTable, searchFieldSx, tableCellSx } from "pages/PayRoll/Salary/Shared/salaryUi";
import { StatCardGrid } from "pages/PayRoll/Shared/statutoryUi";
import { codeName, codeShort, esicRows, formatDay, liabilityOf, money, num, reconStatus } from "./esiReportsData";

// Overview (R1): month totals from the finalised payroll and one row per
// report / ESI code, each with its status and the one action that applies.
// Clicking a row opens its tab.

const STATUS_COLORS = { Available: "#12B76A", Blocked: "#DC2626", Ready: "#2563EB", Generated: "#12B76A", "Action needed": "#DC2626", "All clear": "#12B76A" };
const linkSx = { color: designColors.tealDark, fontWeight: designWeights.semibold, whiteSpace: "nowrap" };

export const EsiOverviewTab = ({ month, monthInfo, rows, codes, issues, allIssues, files, challans, codeRowsFor, goTo }) => {
  const [query, setQuery] = useState("");
  const covered = rows.filter((row) => row.covered);
  const totals = liabilityOf(covered);
  const blocking = issues.filter((item) => item.severity === "Blocking");
  const warnings = issues.filter((item) => item.severity === "Warning").length;
  const infos = issues.filter((item) => item.severity === "Info").length;

  const recon = codes.map((code) => {
    const liability = liabilityOf(codeRowsFor(code)).total;
    const paid = challans.filter((item) => item.code === code.code && item.month === month).reduce((sum, item) => sum + item.amount, 0);
    return { code, status: reconStatus(liability, paid), paid };
  });
  const paidCount = recon.filter((item) => item.status === "Paid" || item.status === "Overpaid").length;

  const reports = [
    {
      report: "Contribution Register",
      code: "All codes",
      details: `${num(covered.length)} covered employees · totals match payroll`,
      status: "Available",
      action: "View · Download",
      tab: "register",
    },
    ...codes.map((code) => {
      const blockers = allIssues.filter((item) => item.severity === "Blocking" && item.code?.code === code.code);
      const file = files.find((item) => item.code === code.code && item.month === month && item.status === "Current");
      const fileRows = esicRows(month, code);
      const ipMissing = blockers.filter((item) => item.rule === "IP_MISSING").length;
      return {
        report: "ESIC File",
        code: codeShort(code),
        details: blockers.length
          ? `Blocked by ${blockers.length} error${blockers.length === 1 ? "" : "s"}${ipMissing ? ` (IP number missing${ipMissing > 1 ? ` × ${ipMissing}` : ""})` : ""}`
          : file
            ? `Version ${file.version} · ${num(fileRows.length)} rows · generated ${formatDay(file.generated_at)}`
            : `${num(fileRows.length)} rows · ready to generate`,
        status: blockers.length ? "Blocked" : file ? "Generated" : "Ready",
        action: blockers.length ? "See validation" : file ? "Download" : "Generate",
        tab: blockers.length ? "validation" : "esic",
      };
    }),
    {
      report: "Reconciliation",
      code: "All codes",
      details: recon
        .map((item) => (item.status === "Not recorded" ? `${codeName(item.code)} not yet recorded` : `${codeName(item.code)} paid ${money(item.paid)}`))
        .join(" · "),
      status: `${paidCount} of ${recon.length} paid`,
      action: "View",
      tab: "reconciliation",
    },
    {
      report: "Validation",
      code: "All codes",
      details: `${blocking.length} blocking · ${warnings} warning${warnings === 1 ? "" : "s"} · ${infos} info`,
      status: blocking.length ? "Action needed" : "All clear",
      action: "View",
      tab: "validation",
    },
  ];
  const needle = query.trim().toLowerCase();
  const shown = needle ? reports.filter((item) => Object.values(item).some((value) => String(value).toLowerCase().includes(needle))) : reports;
  const statusColor = (status) => STATUS_COLORS[status] || (status.startsWith(`${recon.length} of`) ? "#12B76A" : "#F0954A");

  return (
    <Mui.Stack spacing={2}>
      <StatCardGrid
        cards={[
          { key: "covered", title: "Covered employees", value: num(covered.length), icon: GroupsOutlined, tone: "blue" },
          { key: "wages", title: "ESI wages", value: money(covered.reduce((sum, row) => sum + row.wage, 0)), icon: CurrencyRupeeOutlined, tone: "green" },
          { key: "employee", title: "Employee share", value: money(totals.employee), icon: PersonOutlineOutlined, tone: "purple" },
          { key: "employer", title: "Employer share", value: money(totals.employer), icon: AccountBalanceOutlined, tone: "amber" },
          { key: "total", title: "Total liability", value: money(totals.total), icon: FunctionsOutlined, tone: "pink" },
        ]}
      />
      <Mui.Box sx={detailCardSx}>
        <Mui.Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1} sx={{ mb: 1.5 }}>
          <Mui.Typography sx={designType.sectionHeading}>Reports — {monthInfo.label}</Mui.Typography>
          <Mui.TextField size="small" placeholder="Search" value={query} onChange={(event) => setQuery(event.target.value)} sx={searchFieldSx} />
        </Mui.Stack>
        <SimpleTable
          fit
          heads={[
            { label: "Report", width: 170 },
            { label: "ESI Code", width: 200 },
            { label: "Details", width: 320 },
            { label: "Status", width: 130 },
            { label: "Action", width: 140 },
          ]}
        >
          {shown.map((item) => (
            <Mui.TableRow key={`${item.report}-${item.code}`} hover onClick={() => goTo(item.tab)} sx={{ cursor: "pointer" }}>
              <Mui.TableCell sx={{ ...tableCellSx, fontWeight: designWeights.medium }}>{item.report}</Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>{item.code}</Mui.TableCell>
              <Mui.TableCell sx={{ ...tableCellSx, color: designColors.slate600 }}>{item.details}</Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>
                <RequestStatusChip status={item.status} colorCode={statusColor(item.status)} />
              </Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>
                <Mui.Box component="span" sx={linkSx}>{item.action}</Mui.Box>
              </Mui.TableCell>
            </Mui.TableRow>
          ))}
        </SimpleTable>
        <Mui.Typography sx={{ ...designType.captionText, mt: 1.5 }}>
          Reports are live views of the finalised payroll. Only generated ESIC files are stored, with version and timestamp.
        </Mui.Typography>
      </Mui.Box>
    </Mui.Stack>
  );
};
